import type { ReactNode } from 'react';

export interface Column<Row> {
  /** Stable key, used for React keys and the mobile data-label. */
  key: string;
  header: string;
  /** Right-aligned tabular figure. Every money and count column wants this. */
  numeric?: boolean;
  render: (row: Row) => ReactNode;
  /** Hidden below the card breakpoint; use for genuinely secondary columns. */
  secondary?: boolean;
  /**
   * A column an operator may not turn off in the Columns picker.
   *
   * For the one or two that make a row IDENTIFIABLE — a reference, a name.
   * Without it somebody can hide every column that says which row they are
   * looking at and be left with a grid of amounts belonging to nobody.
   *
   * Enforced in the picker, which is where the column set lives. The store
   * behind it holds keys and knows nothing about them, and says so.
   */
  required?: boolean;
}

export interface DataTableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Announced to screen readers; visually hidden. */
  caption: string;
  onRowClick?: (row: Row) => void;
  /**
   * Whether this row opens anything, for a list where not every row does — a row whose screen the
   * reader cannot open. Such a row is drawn as plain cells: no button role, nothing to tab to, no
   * pointer, and no click. Omitted, every row opens when `onRowClick` is set.
   */
  rowClickable?: (row: Row) => boolean;
  /** Rendered in place of the table when there are no rows. */
  empty?: ReactNode;
}

/**
 * The console's list surface.
 *
 * Below 900px each row collapses to a card and every cell prints its column
 * header beside its value, carried in `data-label`. A horizontally scrolling
 * table on a phone hides the columns that matter — an operator swiping to find
 * the amount is an operator who will approve the wrong withdrawal.
 *
 * The table always scrolls inside its OWN container, never the page body.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  caption,
  onRowClick,
  rowClickable,
  empty,
}: DataTableProps<Row>) {
  if (rows.length === 0 && empty) return <>{empty}</>;
  /* The click for this row, or none: a row that opens nothing is not a control. */
  const clickFor = (row: Row): (() => void) | undefined =>
    onRowClick && (rowClickable?.(row) ?? true) ? () => onRowClick(row) : undefined;

  return (
    <div className="table-scroll">
      <table className="table responsive">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={c.numeric ? 'num' : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const open = clickFor(row);
            return (
              <tr
                key={rowKey(row)}
                onClick={open}
                // A clickable row is a real control: it must be reachable and
                // operable from the keyboard, not mouse-only.
                tabIndex={open ? 0 : undefined}
                role={open ? 'button' : undefined}
                onKeyDown={
                  open
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          open();
                        }
                      }
                    : undefined
                }
                style={open ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-label={c.header}
                    className={[c.numeric && 'num', c.secondary && 'secondary']
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
